import React, { useState, useRef, useEffect, useContext } from "react";
import { AppStateContext } from "../../state/AppProvider";
import { Spinner, ISpinnerStyles } from "@fluentui/react";
import { ButtonTooltip } from "../ButtonTooltip";
import { ImageInput } from "../../api";

interface Props {
    onSend: (question: string, images?: ImageInput[]) => void;
    disabled: boolean;
    placeholder?: string;
    clearOnSend?: boolean;
    showToEnd?: boolean;
    toEnd?: () => void;
    fileInputRef?: React.RefObject<HTMLInputElement>;
    uploadFile?: () => void;
    onStop?: () => void;
    isStreaming?: boolean;
    isLoading?: boolean;
}

export const QuestionInput = ({
    onSend,
    disabled,
    placeholder,
    clearOnSend,
    showToEnd,
    toEnd,
    fileInputRef,
    uploadFile,
    onStop,
    isStreaming,
    isLoading
}: Props) => {
    const appStateContext = useContext(AppStateContext);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);

    const [question, setQuestion] = useState<string>("");
    const [images, setImages] = useState<ImageInput[]>([]);
    const [actionsOpen, setActionsOpen] = useState(false);

    // attachments
    const attachments = appStateContext?.state.assistant?.attachments ?? [];
    const hasGetText = attachments.includes("getText");
    const hasUploadImage = attachments.includes("uploadImage");

    const allowFiles = hasGetText;
    const allowImages = hasUploadImage;

    const showPlus = (hasGetText || hasUploadImage) && (hasGetText ? 1 : 0) + (hasUploadImage ? 1 : 0) > 1;
    const showSingleInline = (hasGetText || hasUploadImage) && !showPlus;

    const MAX_IMAGES = 3;

    const canSend = !disabled && !!question.trim();
    const showStop = !!isStreaming;

    const calculateRows = (): void => {
        const textArea = textAreaRef.current;
        if (textArea) {
            textArea.style.height = "auto";
            const scrollHeight = textArea.scrollHeight;
            textArea.style.height = `${scrollHeight}px`;
        }
    };

    // Recalculate rows whenever the text changes
    useEffect(() => {
        calculateRows();
    }, [textAreaRef?.current?.value]);

    useEffect(() => {
        if ((!showPlus || disabled) && actionsOpen) setActionsOpen(false);
    }, [disabled, actionsOpen, showPlus]);

    useEffect(() => {
        if (!allowImages && images.length) setImages([]);
    }, [allowImages]);

    const readFilesAsDataUrls = async (files: FileList | File[]) => {
        const remaining = Math.max(0, MAX_IMAGES - images.length);
        if (remaining === 0) {
            alert(`You can attach up to ${MAX_IMAGES} images.`);
            return;
        }
        const fileArr = Array.from(files)
            .filter(f => f.type.startsWith("image/"))
            .slice(0, remaining);
        const reads = fileArr.map(
            f =>
                new Promise<ImageInput>((resolve, reject) => {
                    const fr = new FileReader();
                    fr.onload = () => resolve({ name: f.name, dataUrl: String(fr.result), mimeType: f.type, size: f.size });
                    fr.onerror = reject;
                    fr.readAsDataURL(f);
                })
        );
        const results = await Promise.all(reads);
        setImages(prev => [...prev, ...results]);
        const dropped = Array.from(files).filter(f => f.type.startsWith("image/")).length - fileArr.length;
        if (dropped > 0) alert(`Only ${MAX_IMAGES} images allowed. ${dropped} not added.`);
    };

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }

        onSend(question, allowImages ? images : undefined);

        if (clearOnSend) {
            setQuestion("");
            setImages([]);
            setActionsOpen(false);
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onPrimaryClick = () => {
        if (showStop) onStop?.();
        else if (canSend) sendQuestion();
    };

    const onQuestionChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        calculateRows();
        if (!ev.target.value) {
            setQuestion("");
        } else {
            setQuestion(ev.target.value);
        }
    };

    const sendQuestionDisabled = disabled || !question.trim();

    const onPaste = (ev: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (!allowImages) return;
        const files = ev.clipboardData?.files;
        if (files && files.length > 0) {
            readFilesAsDataUrls(files);
        }
    };

    const onUploadImageClick = () => {
        if (images.length >= MAX_IMAGES) {
            alert(`You can attach up to ${MAX_IMAGES} images.`);
            return;
        }
        imageInputRef.current?.click();
    };

    const onImagesSelected = (ev: React.ChangeEvent<HTMLInputElement>) => {
        if (ev.target.files && ev.target.files.length > 0) {
            readFilesAsDataUrls(ev.target.files);
            ev.target.value = "";
        }
    };

    const removeImage = (index: number) => {
        setImages(prev => prev.filter((_, i) => i !== index));
    };

    const onUploadFileClick = () => {
        fileInputRef?.current?.click();
    };

    const spinnerStyles: ISpinnerStyles = {
        circle: {
            borderColor: "#0062b8 #0062b8 #bee1ff00 #bee1ff00",
            height: "16px",
            width: "16px"
        }
    };

    const removeFile = (index: number) => {
        appStateContext?.dispatch({ type: "REMOVE_FILE", payload: index });
        if (fileInputRef?.current) fileInputRef.current.value = "";
    };

    const ICON_PX = 28;
    const GAP_PX = 8;
    const PAD_NO_PLUS = 16;
    const PAD_WITH_PLUS_COLLAPSED = 48;
    const PAD_ATTACH_ONLY = 48;
    const isVegaDemo = appStateContext?.state.assistant?.assistant === "hrdemo";
    const extraIcons = (allowImages ? 1 : 0) + (allowFiles ? 1 : 0);
    const leftPadPx = showPlus
        ? actionsOpen
            ? PAD_WITH_PLUS_COLLAPSED + extraIcons * (ICON_PX + GAP_PX)
            : PAD_WITH_PLUS_COLLAPSED
        : showSingleInline
          ? PAD_ATTACH_ONLY
          : isVegaDemo
            ? 47
            : PAD_NO_PLUS;

    const actionsWidthClasses = actionsOpen
        ? extraIcons >= 3
            ? "max-w-[176px] opacity-100"
            : extraIcons === 2
              ? "max-w-[120px] opacity-100"
              : extraIcons === 1
                ? "max-w-[64px] opacity-100"
                : "max-w-0 opacity-100"
        : "max-w-0 opacity-0";

    return (
        <div className={"transition-[padding] duration-200 text-base m-auto px-4 " + (appStateContext?.state.isChatHistoryOpen ? "" : "lg:px-16")}>
            {showToEnd && (
                <div
                    className={"absolute left-0 right-0 z-[4] mx-auto w-10 animate-bounce"}
                    style={{ bottom: textAreaRef.current ? textAreaRef.current.scrollHeight + 40 : 92 }}
                >
                    <div className="flex cursor-pointer justify-center" onClick={toEnd}>
                        <svg width="32" height="32" viewBox="0 0 40 40" fill="#FFFFFF" xmlns="http://www.w3.org/2000/svg" className="">
                            <rect x="1" y="1" width="38" height="38" rx="19" stroke="#28373A" stroke-width="2"></rect>
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M20 22.7092L26.532 16.5L28 17.8954L20 25.5L12 17.8954L13.468 16.5L20 22.7092Z"
                                fill="#28373A"
                            ></path>
                        </svg>
                    </div>
                </div>
            )}

            <div className="mx-auto flex flex-1 text-base gap-4 lg:gap-6 lg:max-w-3xl">
                <div className="flex w-full items-center">
                    <div className="overflow-hidden flex flex-col w-full flex-grow relative rounded-lg bg-eq-white shadow-[0px_8px_16px_rgba(0,0,0,0.14),0px_0px_2px_rgba(0,0,0,0.12)]">
                        {allowImages && images.length > 0 && (
                            <div className="flex flex-wrap gap-2 p-3">
                                {images.map((img, idx) => (
                                    <div key={idx} className="relative flex items-center gap-2 rounded border border-eq-mid-grey/40 px-2 py-1">
                                        <img src={img.dataUrl} alt={img.name} className="h-8 w-8 rounded object-cover" />
                                        <span className="text-eq-grey text-xs max-w-[12rem] truncate">{img.name}</span>
                                        <ButtonTooltip title="Remove image" placement="top">
                                            <button className="flex justify-center font-semibold text-eq-green" onClick={() => removeImage(idx)}>
                                                <span className="material-symbols-outlined text-[1.1rem]">cancel</span>
                                            </button>
                                        </ButtonTooltip>
                                    </div>
                                ))}
                            </div>
                        )}

                        {allowFiles &&
                            appStateContext?.state.files &&
                            appStateContext?.state.files.length > 0 &&
                            appStateContext?.state.files
                                .filter(fileItem => fileItem.new)
                                .map((fileItem, index) => (
                                    <a key={index} className="gap-2 w-fit flex pl-3.5 pt-3.5 pb-1 items-center">
                                        {appStateContext?.state.files[index].status === "Loading" && (
                                            <span className="flex justify-center items-center h-[24px] w-[24px]">
                                                <Spinner style={{ display: "block", verticalAlign: "middle" }} styles={spinnerStyles} />
                                            </span>
                                        )}
                                        <span className={appStateContext?.state.files[index].status === "Error" ? "text-eq-green" : "text-eq-grey"}>
                                            {(appStateContext?.state.files[index].status === "Error" ? "Unable to upload " : "") + fileItem.file.name}
                                        </span>
                                        <ButtonTooltip title="Remove file" placement="top">
                                            <button className={"flex justify-center font-semibold text-eq-green"} onClick={() => removeFile(index)}>
                                                <span className="material-symbols-outlined text-[1.25rem]">cancel</span>
                                            </button>
                                        </ButtonTooltip>
                                    </a>
                                ))}

                        <textarea
                            ref={textAreaRef}
                            className={
                                "m-0 w-full resize-none border-0 bg-transparent focus-visible:outline-0 focus:ring-0 focus-visible:ring-0 py-3.5 pr-12 placeholder-gray-300 max-h-[200px] min-h-[52px] transition-[padding] duration-200 " +
                                (showPlus || showSingleInline ? "pl-12" : "pl-4") +
                                (disabled ? " opacity-60 cursor-default" : "")
                            }
                            placeholder={placeholder}
                            rows={1}
                            value={question}
                            onChange={onQuestionChange}
                            onKeyDown={onEnterPress}
                            onPaste={onPaste}
                            style={{ paddingLeft: leftPadPx }}
                            disabled={disabled}
                        ></textarea>

                        <>
                            {allowImages && <input className="hidden" type="file" accept="image/*" multiple ref={imageInputRef} onChange={onImagesSelected} />}
                            {allowFiles && (
                                <input
                                    className="hidden"
                                    type="file"
                                    accept=".pdf,.doc,.docx,.txt"
                                    ref={fileInputRef}
                                    onChange={uploadFile}
                                    multiple
                                    maxLength={2}
                                />
                            )}

                            {showPlus && (
                                <div className="absolute bottom-3 left-3 flex items-center h-[28px]">
                                    <button
                                        className={
                                            "flex h-7 w-7 items-center justify-center rounded-full shadow-sm transition-colors " +
                                            (disabled ? "text-eq-mid-grey cursor-default" : "text-eq-green cursor-pointer")
                                        }
                                        onClick={!disabled ? () => setActionsOpen(o => !o) : undefined}
                                    >
                                        <span
                                            className={
                                                "material-symbols-outlined text-[1.25rem] text-eq-green transition-transform duration-200 " +
                                                (actionsOpen ? "rotate-45" : "")
                                            }
                                        >
                                            add_2
                                        </span>
                                    </button>

                                    <div className={"ml-2 flex items-center gap-2 overflow-hidden transition-all duration-200 " + actionsWidthClasses}>
                                        {allowImages && (
                                            <ButtonTooltip title={images.length >= MAX_IMAGES ? `Max ${MAX_IMAGES} images` : "Attach images"} placement="top">
                                                <button
                                                    className={
                                                        "text-[1.75rem] material-symbols-outlined transition-transform duration-200 flex items-center " +
                                                        (disabled || images.length >= MAX_IMAGES
                                                            ? "text-eq-mid-grey cursor-default"
                                                            : "text-eq-green cursor-pointer") +
                                                        (actionsOpen ? " translate-x-0" : " -translate-x-2")
                                                    }
                                                    onClick={!disabled && images.length < MAX_IMAGES ? onUploadImageClick : undefined}
                                                >
                                                    image
                                                </button>
                                            </ButtonTooltip>
                                        )}

                                        {allowFiles && (
                                            <ButtonTooltip title="Attach files" placement="top">
                                                <button
                                                    className={
                                                        "text-[1.75rem] material-symbols-outlined transition-transform duration-200 flex items-center " +
                                                        (disabled ? "text-eq-mid-grey cursor-default" : "text-eq-green cursor-pointer") +
                                                        (actionsOpen ? " translate-x-0" : " -translate-x-2")
                                                    }
                                                    onClick={!disabled ? onUploadFileClick : undefined}
                                                >
                                                    attach_file
                                                </button>
                                            </ButtonTooltip>
                                        )}
                                    </div>
                                </div>
                            )}

                            {showSingleInline && (
                                <div className="absolute bottom-3 left-3 flex items-center h-[28px]">
                                    {allowImages && !allowFiles && (
                                        <ButtonTooltip title={images.length >= MAX_IMAGES ? `Max ${MAX_IMAGES} images` : "Attach images"} placement="top">
                                            <a
                                                className={
                                                    "text-[1.75rem] material-symbols-outlined transition-transform duration-200 flex items-center " +
                                                    (disabled || images.length >= MAX_IMAGES
                                                        ? "text-eq-mid-grey cursor-default"
                                                        : "text-eq-green cursor-pointer")
                                                }
                                                onClick={!disabled && images.length < MAX_IMAGES ? onUploadImageClick : undefined}
                                            >
                                                image
                                            </a>
                                        </ButtonTooltip>
                                    )}

                                    {allowFiles && !allowImages && (
                                        <ButtonTooltip title="Attach files" placement="top">
                                            <a
                                                className={
                                                    "text-[1.75rem] material-symbols-outlined transition-transform duration-200 flex items-center " +
                                                    (disabled ? "text-eq-mid-grey cursor-default" : "text-eq-green cursor-pointer")
                                                }
                                                onClick={!disabled ? onUploadFileClick : undefined}
                                            >
                                                attach_file
                                            </a>
                                        </ButtonTooltip>
                                    )}
                                </div>
                            )}
                        </>

                        {/*enter button*/}
                        <div
                            className={
                                "absolute bottom-3 right-3 h-[28px] rounded-lg " + (showStop ? "bg-rose-600" : canSend ? "bg-eq-green" : "bg-eq-mid-grey")
                            }
                        >
                            <a
                                className={
                                    "text-[1.75rem] material-symbols-outlined text-eq-white " + (showStop || canSend ? "cursor-pointer" : "cursor-default")
                                }
                                onClick={onPrimaryClick}
                            >
                                {showStop ? "stop" : "keyboard_arrow_up"}
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div className="relative px-2 py-2 text-center text-xs text-eq-charcoal">
                <span className="lg:hidden inline-block">Content provided by AskEllie is AI-generated.</span>
                <span className="hidden lg:inline-block">Content provided by AskEllie is AI-generated. Consider checking important information.</span>
            </div>
        </div>
    );
};
